import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { 
    FiX, FiTruck, FiTrendingUp, FiDollarSign, FiCalendar, FiTarget 
} from 'react-icons/fi';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import styles from './ResumoMensal.module.css';

ChartJS.register(ArcElement, Tooltip, Legend);

const ResumoMensalPopup = ({ equipeId, nomeEquipe, onClose }) => {
    const [detalhes, setDetalhes] = useState([]);
    const [financeiro, setFinanceiro] = useState({ despesa: 0, receita: 0, trabalhadas: 0 });
    const [loading, setLoading] = useState(false);
    
    const [mesSelecionado, setMesSelecionado] = useState(new Date().getMonth() + 1);
    const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear());

    const token = localStorage.getItem('token');

    const anosDisponiveis = useMemo(() => {
        const anoAtual = new Date().getFullYear();
        return Array.from({ length: 6 }, (_, i) => anoAtual - i);
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const dataInicio = `${anoSelecionado}-${String(mesSelecionado).padStart(2, '0')}-01`;
            const dataFim = `${anoSelecionado}-${String(mesSelecionado).padStart(2, '0')}-${new Date(anoSelecionado, mesSelecionado, 0).getDate()}`;

            const headers = { Authorization: `Bearer ${token}` };
            const params = { dataInicio, dataFim };

            // CHAMADAS ATUALIZADAS PARA AS NOVAS ROTAS
            const [resProd, resDespesas, resReceitas, resHoras] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/relatorios/equipe/${equipeId}/detalhado`, { headers, params }),
                axios.get(`${API_BASE_URL}/api/despesas-equipe/consolidado/${equipeId}`, { headers, params }),
                axios.get(`${API_BASE_URL}/api/relatorios/equipe/${equipeId}/financeiro-consolidado`, { headers, params }),
                axios.get(`${API_BASE_URL}/api/relatorios/equipe/${equipeId}/metricas-horas`, { headers, params })
            ]);

            const totalDespesa = resDespesas.data.reduce((acc, curr) => acc + parseFloat(curr.valor || 0), 0);
            
            // Ajustado para 'valor_receita' conforme o novo alias do backend
            const totalReceita = resReceitas.data.reduce((acc, curr) => acc + parseFloat(curr.valor_receita || 0), 0);

            setDetalhes(resProd.data);
            setFinanceiro({ 
                despesa: totalDespesa,
                receita: totalReceita,
                // Ajustado para 'total_horas_trabalho' conforme o novo alias do backend
                trabalhadas: resHoras.data.total_horas_trabalho || 0
            });

        } catch (err) {
            console.error("Erro ao carregar dados do fechamento:", err);
        } finally {
            setLoading(false);
        }
    }, [equipeId, anoSelecionado, mesSelecionado, token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const indicadores = useMemo(() => {
        const prodTotal = detalhes.reduce((acc, curr) => acc + parseFloat(curr.producao_diaria_valor || 0), 0) || 0;
        let somaHorasTotal = 0;

        detalhes.forEach(reg => {
            const hTrab = parseFloat(reg.horimetro_final || 0) - parseFloat(reg.horimetro_inicial || 0);
            const hParadas = parseFloat(reg.horas_paradas || 0);
            const hCaminhao = parseFloat(reg.horas_puxar_caminhao || 0);
            const hMadeira = parseFloat(reg.horas_puxar_madeira || 0);
            somaHorasTotal += (hTrab + hParadas + hCaminhao + hMadeira);
        });

        const horasTotaisNoPeriodo = somaHorasTotal || 1;
        const diasTrabalhados = new Set(detalhes.map(reg => reg.data)).size || 1;

        const receitaTotal = parseFloat(financeiro.receita || 0);
        const custoTotal = parseFloat(financeiro.despesa || 0);
        const lucroTotal = receitaTotal - custoTotal;

        return {
            prodTotal,
            horasTotaisNoPeriodo,
            receitaTotal,
            custoTotal,
            lucroTotal,
            custoPorM3: prodTotal > 0 ? custoTotal / prodTotal : 0,
            custoPorHora: custoTotal / horasTotaisNoPeriodo,
            faturamentoPorHora: receitaTotal / horasTotaisNoPeriodo,
            faturamentoPorDia: receitaTotal / diasTrabalhados, 
            lucroPorHora: lucroTotal / horasTotaisNoPeriodo,
            producaoPorHora: prodTotal / horasTotaisNoPeriodo,
            producaoPorDia: prodTotal / diasTrabalhados 
        };
    }, [detalhes, financeiro]);

    return (
        <div className={styles.modalBackdrop}>
            <div className={styles.modalContent}>
                <header className={styles.modalHeader}>
                    <div className={styles.headerTitle}>
                        <FiTarget />
                        <div>
                            <h2>Fechamento Mensal Automático</h2>
                            <p>{nomeEquipe} • Indicadores de Performance</p>
                        </div>
                    </div>
                    <button onClick={onClose} className={styles.closeBtn}><FiX /></button>
                </header>

                <div className={styles.modalBody}>
                    {/* ... (filtros e KPIs permanecem iguais) ... */}
                    <div className={styles.topFilters}>
                        <div className={styles.filterGroup}>
                            <label><FiCalendar /> Período de Análise:</label>
                            <div className={styles.selectsRow}>
                                <select value={mesSelecionado} onChange={e => setMesSelecionado(Number(e.target.value))}>
                                    {["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"].map((m, i) => (
                                        <option key={i} value={i + 1}>{m}</option>
                                    ))}
                                </select>
                                <select value={anoSelecionado} onChange={e => setAnoSelecionado(Number(e.target.value))}>
                                    {anosDisponiveis.map(ano => <option key={ano} value={ano}>{ano}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className={styles.totalHoursBadge}>
                            <span>Horas Totais no Período</span>
                            <strong>{indicadores.horasTotaisNoPeriodo.toFixed(1)}h</strong>
                        </div>
                    </div>

                    <div className={styles.kpiGrid}>
                        <div className={styles.kpiCard}>
                            <label>Receita Bruta</label>
                            <h3 className={styles.textPositive}>R$ {indicadores.receitaTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</h3>
                        </div>
                        <div className={styles.kpiCard}>
                            <label>Custo Total</label>
                            <h3 className={styles.textNegative}>R$ {indicadores.custoTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</h3>
                        </div>
                        <div className={styles.kpiCard}>
                            <label>Resultado Líquido</label>
                            <h3 className={styles.textBlue}>R$ {indicadores.lucroTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</h3>
                        </div>
                    </div>

                    <div className={styles.metricsGrid}>
                        <div className={styles.metricGroup}>
                            <h4><FiDollarSign /> Custos e Médias</h4>
                            <div className={styles.metricRow}><span>Custo por m³:</span><strong>R$ {indicadores.custoPorM3.toFixed(2)}</strong></div>
                            <div className={styles.metricRow}><span>Custo por Hora:</span><strong>R$ {indicadores.custoPorHora.toFixed(2)}</strong></div>
                            <div className={styles.metricRow}><span>Lucro por Hora:</span><strong className={styles.textBlue}>R$ {indicadores.lucroPorHora.toFixed(2)}</strong></div>
                        </div>

                        <div className={styles.metricGroup}>
                            <h4><FiTrendingUp /> Faturamento</h4>
                            <div className={styles.metricRow}><span>Faturamento/Hora:</span><strong>R$ {indicadores.faturamentoPorHora.toFixed(2)}</strong></div>
                            <div className={styles.metricRow}>
                                <span>Faturamento/Dia:</span>
                                <strong>R$ {indicadores.faturamentoPorDia.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>
                            </div>
                        </div>

                        <div className={styles.metricGroup}>
                            <h4><FiTruck /> Produção</h4>
                            <div className={styles.metricRow}><span>Produção/Dia:</span><strong>{indicadores.producaoPorDia.toFixed(2)} m³</strong></div>
                            <div className={styles.metricRow}><span>Total Período:</span><strong>{indicadores.prodTotal.toFixed(2)} m³</strong></div>
                            <div className={styles.metricRow}><span>Produção/Hora:</span><strong>{indicadores.producaoPorHora.toFixed(2)} m³</strong></div>
                        </div>
                    </div>

                    <div className={styles.tableSection}>
                        <table className={styles.miniTable}>
                            <thead>
                                <tr>
                                    <th>Data</th>
                                    <th>Veículo</th>
                                    <th>Horas Trab.</th>
                                    <th>Horas Paradas</th>
                                    <th>Produção</th>
                                </tr>
                            </thead>
                            <tbody>
                                {detalhes.map(reg => (
                                    <tr key={reg.id}>
                                        <td>{new Date(reg.data).toLocaleDateString('pt-BR')}</td>
                                        {/* Ajustado para 'modelo_maquina' conforme o novo alias */}
                                        <td>{reg.modelo_maquina}</td>
                                        <td>{(reg.horimetro_final - reg.horimetro_inicial).toFixed(1)}h</td>
                                        <td>{parseFloat(reg.horas_paradas || 0).toFixed(1)}h</td>
                                        <td className={styles.textGreen}>{reg.producao_diaria_valor} m³</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResumoMensalPopup;